import {useCallback, useState} from 'react';

/**
 * Reusable hook that wraps any async action with loading / error state.
 *
 * Usage:
 *   const {loading, error, run, clearError} = useAsyncAction();
 *   await run(() => saveConfig(eid, json), 'Unable to save configuration.');
 */
export const useAsyncAction = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  /**
   * Execute an async action.
   * @param {() => Promise<{ok: boolean, error?: {message: string}}>} action
   * @param {string} [fallbackMessage] — shown when the result is not ok and has no message.
   * @returns {Promise<{ok: boolean, data?: any, error?: any}>} the raw result.
   */
  const run = useCallback(async (action, fallbackMessage = 'An error occurred.') => {
    setLoading(true);
    setError('');
    try {
      const result = await action();
      if (!result.ok) {
        setError(result.error?.message || fallbackMessage);
      }
      return result;
    } catch (err) {
      console.error(err);
      setError(fallbackMessage);
      return {ok: false, data: null, error: err};
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => setError(''), []);

  return {loading, error, run, clearError, setError};
};

